/**
 * Dollars typed by a person, turned into the integer cents the API stores.
 *
 * ## Why this is not `Number(text) * 100`
 *
 * Three ways that loses money, all of them silent:
 *
 * 1. **Binary floating point.** `0.29 * 100` is `28.999999999999996`. A
 *    truncation there pays a cent less per thousand views for the life of a
 *    campaign, and even `Math.round` is only right because nobody has typed a
 *    number big enough yet. The parse below never leaves the integer domain:
 *    it splits on the point and concatenates the digits.
 *
 * 2. **`Number` accepts things a money box must not.** `Number('1e3')` is
 *    `1000`, `Number('0x20')` is `32`, `Number(' ')` is `0`, `Number('')` is
 *    `0`, and `Number('Infinity')` is `Infinity`. A field that turns `1e3`
 *    into a thousand dollars without saying so is a field that will one day
 *    turn a typo into a thousand dollars without saying so.
 *
 * 3. **`NaN` became zero.** The old parser ended `Number.isFinite(amount) ?
 *    Math.round(amount * 100) : 0`, so anything it could not read was
 *    submitted as **$0.00** — and the optional per-creator cap became **no
 *    cap**, which is the same mistake with the opposite sign. Nothing here
 *    guesses: a value that cannot be read is refused with a sentence and the
 *    form does not submit.
 *
 * ## Why `type="text"` and not `type="number"`
 *
 * A `number` input SANITISES: type `5,000.00` into one and `input.value` is
 * the empty string, so the comma case cannot even be seen by the code that
 * would refuse it — it arrives as "blank", which for the cap meant "no cap".
 * The inputs are `text` with `inputMode="decimal"`, the raw string survives,
 * and the rules below are the only thing that decides.
 *
 * ## What is accepted
 *
 * `1234.56`, `1,234.56`, `.5`, `5000`, `$5,000.00`, `  12  `. Commas only
 * where a thousands separator belongs: `1,5` is refused rather than read as
 * `15` by somebody who writes their decimals the European way.
 */

export type DollarParse =
  | { ok: true; cents: number }
  | { ok: false; reason: string };

/** `1234`, `1,234`, or nothing before the point. */
const GROUPED = /^\d{1,3}(?:,\d{3})*$/;
const PLAIN = /^\d*$/;

/**
 * Parse a typed dollar amount.
 *
 * `allowBlank` is what separates "the budget" (required) from "the per-creator
 * cap" (blank means no cap). The caller says which; this never decides.
 */
export function parseDollars(raw: string): DollarParse {
  const text = raw.trim().replace(/^\$/, '').trim();
  if (text === '') return { ok: false, reason: 'Enter an amount.' };
  if (text.startsWith('-')) return { ok: false, reason: 'An amount cannot be negative.' };

  const parts = text.split('.');
  if (parts.length > 2) return { ok: false, reason: 'One decimal point.' };

  const whole = parts[0] ?? '';
  const fraction = parts[1] ?? '';

  if (whole !== '' && !GROUPED.test(whole) && !PLAIN.test(whole)) {
    return {
      ok: false,
      reason: whole.includes(',')
        ? 'Commas only between thousands: 5,000.00.'
        : 'Digits only, with an optional decimal point.',
    };
  }
  if (!PLAIN.test(fraction)) {
    return { ok: false, reason: 'Digits only after the decimal point.' };
  }
  if (whole === '' && fraction === '') return { ok: false, reason: 'Enter an amount.' };
  if (fraction.length > 2) {
    return { ok: false, reason: 'Money stops at cents: at most two decimal places.' };
  }

  const digits = whole.replace(/,/g, '');
  // Integer arithmetic the whole way: no value here is ever a float.
  const cents = Number(`${digits || '0'}${fraction.padEnd(2, '0')}`);
  if (!Number.isSafeInteger(cents)) return { ok: false, reason: 'That is too large.' };
  return { ok: true, cents };
}

/** The same, where blank is a legitimate answer ("no cap", "no fee"). */
export function parseOptionalDollars(raw: string): DollarParse | { ok: true; cents: null } {
  if (raw.trim() === '') return { ok: true, cents: null };
  return parseDollars(raw);
}

/** Cents, back into what belongs in the box. Never grouped: it is an input. */
export function centsToDollarInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '';
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/**
 * The sentence under a money box: what will actually be sent.
 *
 * Not decoration. `0.3` is thirty cents and `5000` is five hundred thousand of
 * them, and the only way an operator can tell which one they just typed before
 * they press the button is if the field says so.
 */
export function centsEcho(cents: number): string {
  const grouped = cents.toLocaleString('en-US');
  const dollars = `$${(Math.abs(cents) / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
  return `Sends ${grouped} cents — ${cents < 0 ? '-' : ''}${dollars}`;
}
